import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const TIPO_LABEL = { portatil: 'Portátil', fixo: 'Fixo', componente_malha: 'Comp. Malha' };
const CAL_STATUS = {
  calibrado:        { label: 'Calibrado',       dot: 'bg-emerald-500', text: 'text-emerald-400' },
  a_vencer:         { label: 'A Vencer',         dot: 'bg-amber-500',   text: 'text-amber-400' },
  vencido:          { label: 'Vencido',          dot: 'bg-red-500',     text: 'text-red-400' },
  bloqueado:        { label: 'Bloqueado',        dot: 'bg-slate-400',   text: 'text-slate-400' },
  em_calibracao:    { label: 'Em Calibração',    dot: 'bg-violet-500',  text: 'text-violet-400' },
  aguardando_laudo: { label: 'Ag. Laudo',        dot: 'bg-blue-400',    text: 'text-blue-400' },
  nao_calibrado:    { label: 'Não Calibrado',    dot: 'bg-slate-600',   text: 'text-slate-500' },
};

function dataFmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
}

function CompliancePill({ pct, ok, total }) {
  if (total === 0) return null;
  const color = pct >= 80 ? 'text-emerald-400 bg-emerald-950/30 border-emerald-800/40'
    : pct >= 50 ? 'text-amber-400 bg-amber-950/30 border-amber-800/40'
    : 'text-red-400 bg-red-950/30 border-red-800/40';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${color}`}>
      {ok}/{total} calibrados
    </span>
  );
}

function LoopModal({ loop, onClose, onSaved }) {
  const [tag, setTag] = useState(loop?.tag || '');
  const [descricao, setDescricao] = useState(loop?.descricao || '');
  const [setor, setSetor] = useState(loop?.setor || '');
  const [variavel, setVariavel] = useState(loop?.variavel || '');
  const [unidade, setUnidade] = useState(loop?.unidade || '');
  const [faixaMin, setFaixaMin] = useState(loop?.faixaMin ?? '');
  const [faixaMax, setFaixaMax] = useState(loop?.faixaMax ?? '');
  const [faixaUnit, setFaixaUnit] = useState(loop?.faixaUnit || '');
  const [observacoes, setObservacoes] = useState(loop?.observacoes || '');
  const [instrumentList, setInstrumentList] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState(
    (loop?.instruments || []).map(i => ({ instrumentId: i.instrumentId, funcao: i.funcao || '' }))
  );
  const [instrSearch, setInstrSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!loop;

  const INPUT = 'w-full px-3 py-2 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg focus:outline-none focus:border-indigo-500 text-sm text-[var(--text-base)]';
  const LABEL = 'block text-[10px] text-slate-500 uppercase tracking-wider mb-1';

  useEffect(() => {
    api.get('/instruments').then(res => setInstrumentList(res.data.dados || res.data || [])).catch(() => {});
  }, []);

  const filteredInstruments = instrumentList.filter(i => {
    const q = instrSearch.toLowerCase();
    return i.tag.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
  });

  const toggleInstrument = (id) => {
    setSelectedInstruments(prev =>
      prev.find(x => x.instrumentId === id)
        ? prev.filter(x => x.instrumentId !== id)
        : [...prev, { instrumentId: id, funcao: '' }]
    );
  };

  const setFuncao = (id, val) =>
    setSelectedInstruments(prev => prev.map(x => x.instrumentId === id ? { ...x, funcao: val } : x));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tag.trim() || !descricao.trim()) { setError('TAG e descrição são obrigatórios'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        tag: tag.trim().toUpperCase(), descricao: descricao.trim(),
        setor: setor || undefined, variavel: variavel || undefined, unidade: unidade || undefined,
        faixaMin: faixaMin !== '' ? parseFloat(faixaMin) : undefined,
        faixaMax: faixaMax !== '' ? parseFloat(faixaMax) : undefined,
        faixaUnit: faixaUnit || undefined, observacoes: observacoes || undefined,
        instruments: selectedInstruments,
      };
      const res = isEdit ? await api.put(`/loops/${loop._id}`, payload) : await api.post('/loops', payload);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar malha');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--surface-4)]">
          <h2 className="text-sm font-semibold text-white">{isEdit ? `Editar Malha — ${loop.tag}` : 'Nova Malha de Controle'}</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-5 flex-1">
          <div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Identificação</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className={LABEL}>TAG *</label>
                <input type="text" value={tag} onChange={(e) => setTag(e.target.value.toUpperCase())} placeholder="Ex: MALHA-001" className={INPUT} required />
              </div>
              <div><label className={LABEL}>Setor</label>
                <input type="text" value={setor} onChange={(e) => setSetor(e.target.value)} className={INPUT} />
              </div>
            </div>
            <div><label className={LABEL}>Descrição *</label>
              <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Malha de controle de temperatura do reator" className={INPUT} required />
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Variável de Processo</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className={LABEL}>Variável</label>
                <input type="text" value={variavel} onChange={(e) => setVariavel(e.target.value)} placeholder="Ex: Temperatura" className={INPUT} />
              </div>
              <div><label className={LABEL}>Unidade</label>
                <input type="text" value={unidade} onChange={(e) => setUnidade(e.target.value)} placeholder="Ex: °C" className={INPUT} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={LABEL}>Faixa Mín</label>
                <input type="number" step="any" value={faixaMin} onChange={(e) => setFaixaMin(e.target.value)} className={INPUT} />
              </div>
              <div><label className={LABEL}>Faixa Máx</label>
                <input type="number" step="any" value={faixaMax} onChange={(e) => setFaixaMax(e.target.value)} className={INPUT} />
              </div>
              <div><label className={LABEL}>Unidade da Faixa</label>
                <input type="text" value={faixaUnit} onChange={(e) => setFaixaUnit(e.target.value)} placeholder="Ex: °C" className={INPUT} />
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Instrumentos da Malha <span className="text-slate-600 normal-case font-normal ml-1">({selectedInstruments.length} selecionados)</span>
            </p>
            <input type="text" value={instrSearch} onChange={(e) => setInstrSearch(e.target.value)}
              placeholder="Buscar por TAG ou descrição..." className={`${INPUT} mb-2`} />
            <div className="max-h-48 overflow-y-auto border border-[var(--surface-4)] rounded-lg divide-y divide-[var(--surface-4)]">
              {filteredInstruments.length === 0 && (
                <p className="text-slate-500 text-xs p-3">Nenhum instrumento encontrado</p>
              )}
              {filteredInstruments.map(i => {
                const sel = selectedInstruments.find(x => x.instrumentId === i._id || x.instrumentId === i.id);
                const iid = i._id || i.id;
                return (
                  <div key={iid} className={`flex items-center gap-3 px-3 py-2 transition-colors ${sel ? 'bg-[var(--surface-3)]' : 'hover:bg-[var(--surface-3)]'}`}>
                    <input type="checkbox" checked={!!sel} onChange={() => toggleInstrument(iid)} className="accent-indigo-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-mono font-medium text-indigo-300">{i.tag}</span>
                      <span className="text-xs text-slate-500 ml-2 truncate">{i.description}</span>
                    </div>
                    {sel && (
                      <input type="text" value={sel.funcao} onChange={(e) => setFuncao(iid, e.target.value)}
                        placeholder="Função na malha" className="px-2 py-1 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded text-xs w-40 focus:outline-none focus:border-indigo-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div><label className={LABEL}>Observações</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} className={`${INPUT} resize-none`} />
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-2 justify-end pt-2 border-t border-[var(--surface-4)]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-slate-400 border border-[var(--surface-4)] rounded-lg hover:border-slate-500 hover:text-slate-200 transition-colors">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors">
              {loading ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Malha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoopCard({ loop, onEdit, onDelete, canEdit, canDelete }) {
  const [expanded, setExpanded] = useState(false);
  const pct = loop.compliance;
  const compColor = pct == null ? '' : pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono font-bold text-indigo-300 text-sm">{loop.tag}</span>
            {loop.setor && <span className="text-[10px] bg-[var(--surface-4)] text-slate-400 px-2 py-0.5 rounded">{loop.setor}</span>}
            <CompliancePill pct={pct} ok={loop.calibradosCount} total={loop.calibrableCount} />
          </div>
          <p className="text-sm text-slate-300 truncate">{loop.descricao}</p>

          {(loop.variavel || loop.faixaMin != null) && (
            <p className="text-xs text-slate-600 mt-1">
              {loop.variavel && <span>{loop.variavel}{loop.unidade ? ` (${loop.unidade})` : ''}</span>}
              {loop.faixaMin != null && loop.faixaMax != null && (
                <span className="ml-2 text-slate-500">{loop.faixaMin} – {loop.faixaMax} {loop.faixaUnit || loop.unidade || ''}</span>
              )}
            </p>
          )}

          {/* Barra de conformidade */}
          {pct != null && loop.calibrableCount > 0 && (
            <div className="mt-2 h-1 bg-[var(--surface-4)] rounded-full w-40 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${compColor}`} style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-500 bg-[var(--surface-4)] px-2 py-1 rounded">
            {loop.instruments?.length || 0} instr.
          </span>
          {canEdit && (
            <button onClick={() => onEdit(loop)} className="text-xs px-3 py-1 bg-[var(--surface-4)] hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
              Editar
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(loop)} className="text-xs px-3 py-1 bg-red-950/40 hover:bg-red-900/60 text-red-400 rounded-lg transition-colors border border-red-900/30">
              Excluir
            </button>
          )}
          {loop.instruments?.length > 0 && (
            <button onClick={() => setExpanded(x => !x)} className="text-slate-500 hover:text-slate-300 text-xs w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-[var(--surface-4)]">
              {expanded ? '▲' : '▼'}
            </button>
          )}
        </div>
      </div>

      {expanded && loop.instruments?.length > 0 && (
        <div className="border-t border-[var(--surface-4)] px-5 pb-4">
          <table className="w-full text-xs mt-3">
            <thead>
              <tr className="text-[10px] text-slate-600 uppercase tracking-wider border-b border-[var(--surface-4)]">
                <th className="text-left pb-2 pr-4 font-medium">TAG</th>
                <th className="text-left pb-2 pr-4 font-medium">Descrição</th>
                <th className="text-left pb-2 pr-4 font-medium">Tipo</th>
                <th className="text-left pb-2 pr-4 font-medium">Função na Malha</th>
                <th className="text-left pb-2 pr-4 font-medium">Status Calibração</th>
                <th className="text-left pb-2 font-medium">Próx. Calibr.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--surface-4)]/50">
              {loop.instruments.map(li => {
                const cfg = CAL_STATUS[li.calibrationStatus] || { label: li.calibrationStatus || '—', dot: 'bg-slate-600', text: 'text-slate-500' };
                return (
                  <tr key={li.id} className="hover:bg-[var(--surface-4)]/20 transition-colors">
                    <td className="py-2 pr-4 font-mono text-indigo-300 font-medium">{li.tag}</td>
                    <td className="py-2 pr-4 text-slate-300 max-w-[200px] truncate">{li.description}</td>
                    <td className="py-2 pr-4 text-slate-500">{li.tipoInstrumento ? TIPO_LABEL[li.tipoInstrumento] : '—'}</td>
                    <td className="py-2 pr-4 text-slate-400">{li.funcao || '—'}</td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                        <span className={`font-medium ${cfg.text}`}>{cfg.label}</span>
                      </div>
                    </td>
                    <td className={`py-2 text-xs tabular-nums ${li.nextCalibrationDate ? 'text-slate-400' : 'text-slate-600'}`}>
                      {dataFmt(li.nextCalibrationDate)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LoopsPage() {
  const { user } = useAuth();
  const [loops, setLoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSetor, setFilterSetor] = useState('');
  const [modalLoop, setModalLoop] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { can } = usePermissions();
  const canEdit = can('malhas', 'edit');
  const canDel = user?.role === 'supervisor';

  const loadLoops = useCallback(async () => {
    try {
      const res = await api.get('/loops');
      setLoops(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLoops(); }, [loadLoops]);

  const handleSaved = (saved) => {
    setLoops(prev => {
      const idx = prev.findIndex(l => l._id === saved._id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/loops/${confirmDelete._id}`);
      setLoops(prev => prev.filter(l => l._id !== confirmDelete._id));
      setConfirmDelete(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir');
    } finally { setDeleting(false); }
  };

  const setores = [...new Set(loops.map(l => l.setor).filter(Boolean))].sort();

  const filtered = loops.filter(l => {
    const q = search.toLowerCase();
    const matchesSearch = l.tag.toLowerCase().includes(q) || l.descricao.toLowerCase().includes(q) || (l.setor || '').toLowerCase().includes(q);
    const matchesSetor = !filterSetor || l.setor === filterSetor;
    return matchesSearch && matchesSetor;
  });

  // Aggregate stats
  const totalInstr = loops.reduce((a, l) => a + (l.instruments?.length || 0), 0);
  const criticalLoops = loops.filter(l => l.calibrableCount > 0 && l.compliance < 80).length;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-slate-200">Malhas de Controle</h1>
          <p className="text-xs text-slate-500 mt-0.5">{loops.length} malha{loops.length !== 1 ? 's' : ''} · {totalInstr} instrumentos{criticalLoops > 0 ? ` · ${criticalLoops} abaixo de 80%` : ''}</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setModalLoop(null); setShowModal(true); }}
            className="text-xs px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white font-semibold rounded-lg transition-colors"
          >
            + Nova Malha
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por TAG, descrição ou setor..."
          className="bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500 flex-1 min-w-0 max-w-sm"
        />
        {setores.length > 0 && (
          <select
            value={filterSetor}
            onChange={(e) => setFilterSetor(e.target.value)}
            className="bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-2 focus:outline-none focus:border-indigo-500"
          >
            <option value="">Todos os setores</option>
            {setores.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <span className="text-xs text-slate-600">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-[var(--surface-3)] rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-600">
          <p className="text-4xl mb-3 opacity-30">🔗</p>
          <p className="text-sm font-medium">Nenhuma malha encontrada</p>
          {canEdit && <p className="text-xs mt-1">Clique em "+ Nova Malha" para começar</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(loop => (
            <LoopCard
              key={loop._id}
              loop={loop}
              canEdit={canEdit}
              canDelete={canDel}
              onEdit={(l) => { setModalLoop(l); setShowModal(true); }}
              onDelete={setConfirmDelete}
            />
          ))}
        </div>
      )}

      {showModal && (
        <LoopModal
          loop={modalLoop}
          onClose={() => { setShowModal(false); setModalLoop(null); }}
          onSaved={handleSaved}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-white mb-2 text-sm">Excluir Malha</h3>
            <p className="text-xs text-slate-400 mb-4">
              Tem certeza que deseja excluir a malha <span className="font-mono text-indigo-300">{confirmDelete.tag}</span>?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs border border-[var(--surface-4)] text-slate-400 rounded-lg hover:text-slate-200 transition-colors">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting}
                className="px-3 py-1.5 text-xs bg-red-700 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
                {deleting ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
